import React, { useEffect, useRef } from 'react';

import { RowData } from '@tanstack/table-core';

import { styTable } from './style';
import { TableProps } from './types';
import useTable from './hooks/useTable';
import TableHead from './Head';
import TableBody from './Body';
import TableFoot from './Foot';
import useInfiniteScroll from './hooks/useInfiniteScroll';

export { createColumnHelper } from '@tanstack/react-table';

export const DEFAULT_TABLE_BORDER_STYLES = {
  borderWidth: '1px',
  borderColor: '#000',
  borderStyle: 'solid',
};

export const DEFAULT_PLACEHOLDER_COUNT = 4;

const TableInner = <TData extends RowData>({
  className,
  data = [],
  columns = [],

  options = {},

  isInitialLoading,
  isFetchingNext,
  fetchNext,
  getRowId,
  renderRowExpand,
  emptyStateContent,
}: TableProps<TData>) => {
  const { isHeaderVisible, isFooterVisible } = options;
  const tableContainerRef = useRef<HTMLDivElement>(null);

  const table = useTable({ data, columns, options, getRowId, renderRowExpand });
  const { rows } = table.getRowModel();

  const infiniteScroll = useInfiniteScroll({ fetchNext });

  return (
    <div ref={tableContainerRef} className={className} onScroll={(e) => infiniteScroll(e)}>
      <table className={styTable}>
        {isHeaderVisible && <TableHead table={table} />}
        <TableBody
          table={table}
          parentRef={tableContainerRef}
          rows={rows}
          renderRowExpand={renderRowExpand}
          isInitialLoading={isInitialLoading}
          isFetchingNext={isFetchingNext}
          emptyStateContent={emptyStateContent}
        />
        {isFooterVisible && <TableFoot table={table} />}
      </table>
    </div>
  );
};

export const Table = TableInner;

// Uncomment if codebase is converted to TypeScript
// export const Table = React.forwardRef(TableInner) as <DataModel>(
//   props: TableProps<DataModel> & { ref?: React.ForwardedRef<HTMLDivElement> }
// ) => ReturnType<typeof TableInner>;
